import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import type { UserRole } from '../api/types'
import { Avatar } from './ui'
import './Shell.css'

/**
 * The panel chrome from screen 7a: a 236px sidebar with the brand block,
 * navigation with badge counters, and the signed-in user at the foot.
 */

interface NavItem {
  to: string
  label: string
  /** Which counter from the overview to show, if any. */
  badge?: 'pending' | 'applications'
  minimum: UserRole
}

const NAV: NavItem[] = [
  { to: '/', label: 'İcmal', minimum: 'ADMIN' },
  { to: '/statistika', label: 'Statistika', minimum: 'ADMIN' },
  { to: '/novbe', label: 'Şikayət növbəsi', badge: 'pending', minimum: 'MODERATOR' },
  { to: '/istifadeciler', label: 'İstifadəçilər', minimum: 'ADMIN' },
  { to: '/moderatorlar', label: 'Moderatorlar', minimum: 'ADMIN' },
  { to: '/muracietler', label: 'Müraciətlər', badge: 'applications', minimum: 'ADMIN' },
  { to: '/jurnal', label: 'Fəaliyyət jurnalı', minimum: 'ADMIN' },
  { to: '/parametrler', label: 'Parametrlər', minimum: 'ADMIN' },
]

export function Shell() {
  const { user, signOut, can } = useAuth()
  const [counts, setCounts] = useState<{ pending: number; applications: number }>({
    pending: 0,
    applications: 0,
  })

  // The sidebar badges come from the overview endpoint. A moderator who cannot
  // read it simply gets no badges rather than an error.
  useEffect(() => {
    if (!can('ADMIN')) return

    let cancelled = false
    const load = () =>
      api
        .overview()
        .then((data) => {
          if (!cancelled) {
            setCounts({ pending: data.pendingReports, applications: data.newApplications })
          }
        })
        .catch(() => undefined)

    void load()
    // The queue moves while the panel is open; a minute is frequent enough for
    // a counter and cheap enough not to matter.
    const timer = window.setInterval(load, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [can])

  const visible = NAV.filter((item) => can(item.minimum))

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Əsas məzmuna keç
      </a>

      <aside className="sidebar">
        <div className="sidebar__brand">
          <div className="sidebar__brand-mark" aria-hidden="true">
            SB
          </div>
          <div>
            <div className="sidebar__brand-name">SƏBƏB YOXSA BƏHANƏ</div>
            <div className="sidebar__brand-sub">Moderasiya paneli</div>
          </div>
        </div>

        <nav className="sidebar__nav" aria-label="Əsas naviqasiya">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
              }
            >
              <span>{item.label}</span>
              {item.badge && counts[item.badge] > 0 && (
                <span className="sidebar__badge numeral">{counts[item.badge]}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__user">
          <Avatar initials={initials(user?.displayName ?? '?')} />
          <div className="sidebar__user-meta">
            <div className="sidebar__user-name">{user?.displayName}</div>
            <div className="sidebar__user-role">{roleLabel(user?.role)}</div>
          </div>
          <button
            type="button"
            className="sidebar__signout"
            onClick={() => void signOut()}
            title="Hesabdan çıx"
          >
            Çıx
          </button>
        </div>
      </aside>

      <main className="content" id="main">
        <Outlet />
      </main>
    </div>
  )
}

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]!.slice(0, 1) + parts[1]!.slice(0, 1)).toUpperCase()
}

function roleLabel(role: UserRole | undefined): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Baş admin'
    case 'ADMIN':
      return 'Admin'
    case 'SENIOR_MODERATOR':
      return 'Baş moderator'
    case 'MODERATOR':
      return 'Moderator'
    default:
      return 'İstifadəçi'
  }
}
